import { useMemo, useState } from 'react';
import type {
  AfterSignedScanReviewContext,
  AfterSignedScanTimelineChange,
  AfterSignedScanTimelineMode,
} from '../../domain/incidents/afterSignedScanReview';
import {
  applyAfterSignedScanTimelineChange,
  parseAfterSignedScanReviewContext,
  resolveAfterSignedScanWindowBounds,
} from '../../domain/incidents/afterSignedScanReview';

export type IncidentTimePreset = '15m' | '1h' | '24h' | 'today' | 'custom';

export type IncidentTimeRange = {
  fromDate?: string;
  toDate?: string;
  label: string;
};

export type ActiveReviewWindowResolutionState = {
  timePreset?: IncidentTimePreset | null;
  customFrom?: string | null;
  customTo?: string | null;
};

export function toLocalInputValue(iso: string | undefined | null): string {
  const s = String(iso || '').trim();
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function toIsoFromLocalInput(v: string): string | undefined {
  const s = String(v || '').trim();
  if (!s) return undefined;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

/** `type="date"` value (yyyy-mm-dd) for grouped corrective-action next review. */
export function toLocalDateInputValue(iso: string | undefined | null): string {
  const s = String(iso || '').trim();
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function toIsoFromDateInput(v: string): string | undefined {
  const s = String(v || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return undefined;
  const d = new Date(`${s}T12:00:00`);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

/** Demo display: 05/27/2026 without time placeholders. */
export function formatGroupedReviewDateOnlyDisplay(value: string | undefined | null): string | null {
  const localDate = toLocalDateInputValue(value);
  if (localDate) {
    const [year, month, day] = localDate.split('-').map((part) => Number(part));
    if (year && month && day) {
      return new Date(year, month - 1, day).toLocaleDateString(undefined, {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
    }
  }
  const parsed = new Date(String(value || ''));
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
}

/** Normalize datetime-local or ISO strings for stable scope comparisons and URL params. */
export function normalizeScopeWindowValue(value: string | undefined | null): string {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  return toIsoFromLocalInput(trimmed) || trimmed;
}

/** Resolve report window bounds from form state with URL fallbacks (Safety Report deep links). */
export function resolveReportWindowIso(
  reportFrom: string | undefined | null,
  reportTo: string | undefined | null,
  searchParams?: URLSearchParams | null,
): { fromIso?: string; toIso?: string } {
  const fromIso =
    toIsoFromLocalInput(String(reportFrom || ''))
    || normalizeScopeWindowValue(searchParams?.get('report_from') ?? '');
  const toIso =
    toIsoFromLocalInput(String(reportTo || ''))
    || normalizeScopeWindowValue(searchParams?.get('report_to') ?? '');
  return {
    fromIso: fromIso || undefined,
    toIso: toIso || undefined,
  };
}

/**
 * Active incidents review window: prefers hydrated queue/custom timeRange and
 * from_date/to_date deep links over legacy report_from/report_to params.
 */
export function resolveActiveReviewWindowIso(
  searchParams?: URLSearchParams | null,
  timeRange?: Pick<IncidentTimeRange, 'fromDate' | 'toDate'> | null,
  state?: ActiveReviewWindowResolutionState | null,
): { fromIso?: string; toIso?: string } {
  const fromDateIso = normalizeScopeWindowValue(searchParams?.get('from_date') ?? '');
  const toDateIso = normalizeScopeWindowValue(searchParams?.get('to_date') ?? '');
  const reportFromIso = normalizeScopeWindowValue(searchParams?.get('report_from') ?? '');
  const reportToIso = normalizeScopeWindowValue(searchParams?.get('report_to') ?? '');
  const timeRangeFromIso = normalizeScopeWindowValue(timeRange?.fromDate);
  const timeRangeToIso = normalizeScopeWindowValue(timeRange?.toDate);
  const timePreset = String(state?.timePreset || '').trim();
  const customFrom = String(state?.customFrom || '').trim();
  const customTo = String(state?.customTo || '').trim();
  const hasHydratedCustomWindow = Boolean(customFrom || customTo);
  const legacyReportWindowShouldSeedActiveWindow = (
    timePreset === '24h'
    && !hasHydratedCustomWindow
    && !fromDateIso
    && !toDateIso
    && Boolean(reportFromIso || reportToIso)
  );
  const fromIso = legacyReportWindowShouldSeedActiveWindow
    ? (reportFromIso || timeRangeFromIso)
    : (timeRangeFromIso || fromDateIso || reportFromIso);
  const toIso = legacyReportWindowShouldSeedActiveWindow
    ? (reportToIso || timeRangeToIso)
    : (timeRangeToIso || toDateIso || reportToIso);
  return {
    fromIso: fromIso || undefined,
    toIso: toIso || undefined,
  };
}

export type {
  AfterSignedScanReviewContext,
  AfterSignedScanTimelineChange,
  AfterSignedScanTimelineMode,
};

export function readAfterSignedScanReviewFromParams(
  searchParams?: URLSearchParams | null,
): AfterSignedScanReviewContext {
  return parseAfterSignedScanReviewContext(searchParams);
}

export function resolveAfterSignedScanReviewWindow(
  context: AfterSignedScanReviewContext,
  timeRange?: Pick<IncidentTimeRange, 'toDate'> | null,
) {
  return resolveAfterSignedScanWindowBounds(context, timeRange?.toDate);
}

export function writeAfterSignedScanTimelineToParams(
  params: URLSearchParams,
  change: AfterSignedScanTimelineChange,
  anchors: Parameters<typeof applyAfterSignedScanTimelineChange>[2],
): void {
  applyAfterSignedScanTimelineChange(params, change, anchors);
}

export function useIncidentsTimeRange(initialPreset: IncidentTimePreset = '24h') {
  const [timePreset, setTimePreset] = useState<IncidentTimePreset>(initialPreset);
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');

  const timeRange = useMemo<IncidentTimeRange>(() => {
    const now = new Date();
    if (timePreset === '15m') {
      const from = new Date(now.getTime() - 15 * 60 * 1000);
      return { fromDate: from.toISOString(), toDate: now.toISOString(), label: 'Last 15m' };
    }
    if (timePreset === '1h') {
      const from = new Date(now.getTime() - 60 * 60 * 1000);
      return { fromDate: from.toISOString(), toDate: now.toISOString(), label: 'Last 1h' };
    }
    if (timePreset === '24h') {
      const from = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      return { fromDate: from.toISOString(), toDate: now.toISOString(), label: 'Last 24h' };
    }
    if (timePreset === 'today') {
      const startUtc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0));
      return { fromDate: startUtc.toISOString(), toDate: now.toISOString(), label: 'Today (UTC)' };
    }
    const from = toIsoFromLocalInput(customFrom);
    const to = toIsoFromLocalInput(customTo);
    return { fromDate: from, toDate: to, label: 'Custom' };
  }, [timePreset, customFrom, customTo]);

  return {
    timePreset,
    setTimePreset,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    timeRange,
  };
}
